import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import useCartStore from 'store/useCartStore';
import { toast } from 'react-hot-toast'; 
import { useProducts } from 'hooks/useProducts';
import { useBanners } from 'hooks/useBanners'; 
import { AppConfig } from 'config/AppConfig'; 
import { useCompanyData } from 'hooks/useCompanyData';
import { getMainGraphSchema } from 'utils/schemas/mainSchemas';
import { optimizedSeoData } from 'utils/SEO/optimizedSeo';
import { normalizeCompanyInfo } from 'utils/companyMapper';
import HeroSlider from 'components/home/HeroSlider/HeroSlider';
import BannerSlider from 'components/home/HeroSlider/BannerSlider';
import CategoryGrid from 'components/home/FeaturedCategories/CategoryGrid';
import FeaturedBrands from 'components/home/ComercialAllies/FeaturedBrands';
import { getDisdelImageUrl } from 'utils/imageUrl';

const HOME_FALLBACK_TITLE =
  'Suministros y Productos de Limpieza y Mantenimiento de tus instalaciones, Articulos de limpieza, Productos de Limpieza, Elaboracion y Entrega de Kits de donaciones. Atendemos Mayoristas.';
const HOME_FALLBACK_DESCRIPTION =
  'Suministros, Productos y articulos de Limpieza en General, Elaboracion y Entrega de Kits, Gestor de Compras online, Productos sobre demanda, lo que no tenemos lo conseguimos o lo hacemos';

// El contenido visible al abrir el home forma parte del paquete crítico.
// Así evitamos que categorías, marcas y banner aparezcan tarde y provoquen CLS.
const PromoNescafe = lazy(() => import('components/home/PromoNescafe/PromoNescafe'));

//CARGA PEREZOSA : Componentes pesados que están más abajo
const ProductCarousel = lazy(() => import('components/Carousel/ProductCarousel'));
const PromoLayout = lazy(() => import('components/home/PromoLayout/PromoLayout'));
const InfoSection = lazy(() => import('components/home/InfoSection/InfoSection'));
const NewsletterSignup = lazy(() => import('components/home/InfoSection/NewsLetterSignup'));

const HomePage = () => {
  const [loadProducts, setLoadProducts] = useState(false);

  useEffect(() => {
    let idleId;
    let timerId;

    const enableProductCatalog = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(
          () => setLoadProducts(true),
          { timeout: 2000 }
        );
      } else {
        timerId = window.setTimeout(() => setLoadProducts(true), 500);
      }
    };

    if (document.readyState === 'complete') {
      enableProductCatalog();
    } else {
      window.addEventListener('load', enableProductCatalog, { once: true });
    }

    return () => {
      window.removeEventListener('load', enableProductCatalog);
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timerId !== undefined) window.clearTimeout(timerId);
    };
  }, []);
  
  const addItem = useCartStore((state) => state.addItem);
  const { data: allProducts, isLoading } = useProducts({ enabled: loadProducts });
  const { data: bannerData} = useBanners();
  const { data: companyInfo } = useCompanyData();

  const activeCompanyInfo = useMemo(() => {
    if (!Array.isArray(companyInfo) || companyInfo.length === 0) {
      return {};
    }
    
    const rawInfo = companyInfo[0];
    const mappedInfo = normalizeCompanyInfo(rawInfo);

    // 🚀 FUSIÓN DE SEGURIDAD: Mezclamos el mapeador con el objeto bruto de la base de datos
    // Esto asegura que tengamos acceso directo a los backing fields ("<MetaTitle>k__BackingField")
    // incluso si 'normalizeCompanyInfo' no los mapeó explícitamente en el archivo de utilidades.
    return {
      ...rawInfo,
      ...mappedInfo
    };
  }, [companyInfo]);
  
  const homeSeo = useMemo(() => optimizedSeoData['home'] || null, []);
  const fullGraphSchema = useMemo(() => getMainGraphSchema(activeCompanyInfo), [activeCompanyInfo]);

  const schemaJson = useMemo(
    () => JSON.stringify(fullGraphSchema),
    [fullGraphSchema]
  );

  // Título y Descripción dinámicos desde Base de Datos
  const seoTitle = useMemo(() => {
  return (
    activeCompanyInfo.metaTitle ||
    activeCompanyInfo.MetaTitle ||
    homeSeo?.t ||
    HOME_FALLBACK_TITLE
    );
  }, [activeCompanyInfo, homeSeo]);

  const seoDesc = useMemo(() => {
    return (
      activeCompanyInfo.metaDescription ||
      activeCompanyInfo.MetaDescription ||
      activeCompanyInfo.DescripcionCorta || // Campo directo C# SQL de CompaniaWeb
      activeCompanyInfo.descripcionCorta ||
      homeSeo?.d ||
      HOME_FALLBACK_DESCRIPTION
    );
  }, [activeCompanyInfo, homeSeo]);

  // 🚀 NUEVA UNIFICACIÓN DE KEYWORDS Y TAGS DINÁMICOS DEL HOME (Invisibles para humanos)
  const seoKeywords = useMemo(() => {
  const base = homeSeo?.k || "limpieza, suministros, guatemala";

  const extra = [
    activeCompanyInfo.metaKeyword,
    activeCompanyInfo.metaTags,
  ]
    .filter(Boolean)
    .join(", ");

  return extra ? `${base}, ${extra}` : base;
  }, [activeCompanyInfo, homeSeo]);

  const firstHeroImage = useMemo(() => {
  const firstSlide = bannerData?.sliderPrincipal?.[0];
  if (!firstSlide) return '';
  
  // `Imagen` ya está optimizada realmente como WebP. Algunas variantes
  // móviles actuales contienen PNG de varios MB aunque terminen en .webp.
  const imgPath = firstSlide.Imagen || firstSlide.BannerImagenMovil;
    
  return getDisdelImageUrl(imgPath) || '';
  }, [bannerData]);

  const ogImage = useMemo(() => (
    firstHeroImage ||
    "https://disdelsa.com/og-image.jpg"
  ), [firstHeroImage]);

  //OPTIMIZADO DE MEMORIA Y RE-RENDERS
  const handleAddToCart = useCallback((product) => {
    addItem({
      ...product,
      presentationSelected: product.Unidad || 'Unidad',
      unitType: product.Unidad ? 'Y' : 'N'
    });
    toast.success(`${product.Descripcion.substring(0, 20)}... añadido`, {
      position: 'bottom-right',
    });
  }, [addItem]);    

  const carruseles = useMemo(() => {
    if (!allProducts || !Array.isArray(allProducts)) {
        return { higiene: [], coffee: [], cotizados: [] };
    }

    const formatProduct = (p) => ({
      ...p,
      id: p.IdProducto,
      name: p.Descripcion,
      image: getDisdelImageUrl(p.Imagen, 'productos')
    });

    const seenHigiene = new Set(); const seenCoffee = new Set(); const seenCotizados = new Set();
    const higiene = []; const coffee = []; const cotizados = [];

    allProducts.forEach((p) => {
    const pid = String(p.IdProducto);

    if (
        String(p.IdSegmento) === AppConfig.HOME_SEGMENTS.HIGIENE &&
        !seenHigiene.has(pid)
    ) {
        if (higiene.length < 15) {
            higiene.push(formatProduct(p));
            seenHigiene.add(pid);
        }
    }

    if (
        String(p.IdCategoria) === AppConfig.HOME_SEGMENTS.COFFEE_BREAK &&
        !seenCoffee.has(pid)
    ) {
        if (coffee.length < 15) {
            coffee.push(formatProduct(p));
            seenCoffee.add(pid);
        }
    }

    if (cotizados.length < 12 && !seenCotizados.has(pid)) {
        cotizados.push(formatProduct(p));
        seenCotizados.add(pid);
    }
    });

    return { cotizados, higiene, coffee };
  }, [allProducts]);

  // La consulta se activa al quedar libre el hilo principal. Mientras tanto se
  // conserva el mismo espacio del carrusel final para que el resto del home no salte.
  const productsPending = !loadProducts || isLoading;

  return (
    <main>
      <Helmet>
        {/* --- 🚀 SEO TÉCNICO B2B --- */}
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />

        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"/>
        <meta name="author" content="Disdel, S.A."/>
        <meta name="generator" content="React" />
        <link rel="canonical" href="https://disdelsa.com/" />


        {/* --- OPEN GRAPH (Facebook, WhatsApp, LinkedIn) --- */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc}/>
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content="https://disdelsa.com/" />

        <meta property="og:image:width" content="1200"/>
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Disdel" />

        {/* --- TWITTER CARD --- */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc}/>
        <meta name="twitter:image" content={ogImage}/>

        {/* --- SCHEMAS --- */}
        <script type="application/ld+json">
          {schemaJson}
        </script>
      </Helmet>

      <h1 style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        border: '0'
        }}>
        {seoTitle}
      </h1>

      <HeroSlider />

      <CategoryGrid /> 
      <FeaturedBrands />
      <BannerSlider />

    {/* 3. BLOQUE DE CAROUSELES (Como usan el mismo componente ProductCarousel, se envuelven juntos) */}
    <Suspense fallback={<div className="home-carousel-reservation home-carousel-reservation--double" aria-hidden="true" />}>
      {(productsPending || carruseles.cotizados.length > 0) && (
        <div className="carousel-wrapper" data-nosnippet>
          <ProductCarousel
            title="Los más Cotizados"
            products={carruseles.cotizados}
            addToCart={handleAddToCart} 
            variant="carousel-cotizados"
            isLoading={productsPending} 
          />
        </div>
      )}

      {(productsPending || carruseles.higiene.length > 0) && (
        <div className="carousel-wrapper" data-nosnippet>
          <ProductCarousel
            title="Soluciones integrales de higiene"
            products={carruseles.higiene}
            addToCart={handleAddToCart} 
            variant="carousel-higiene"
            isLoading={productsPending}
            viewAllUrl="/categoria/banos-e-higiene/dispensadores-y-accesorios"
          />
        </div>
      )}
    </Suspense>

    <Suspense fallback={null}>
      <PromoNescafe />
    </Suspense>

    {/* 4. ÚLTIMO BLOQUE DE LA PÁGINA (Componentes del pie de página) */}
    <Suspense fallback={<div className="home-carousel-reservation" aria-hidden="true" />}>
      {(productsPending || carruseles.coffee.length > 0) && (
        <div className="carousel-wrapper" data-nosnippet>
          <ProductCarousel
            title="Insumos de Cafetería"
            products={carruseles.coffee}
            addToCart={handleAddToCart} 
            variant="carousel-coffe"
            isLoading={productsPending}
            viewAllUrl="/categoria/cafeteria/cafe-y-complementos" 
          />
        </div>
      )}
    </Suspense>

    <Suspense fallback={null}>
      <PromoLayout />
      <NewsletterSignup />
      <InfoSection />
    </Suspense>
    </main>
  );
};

export default React.memo(HomePage);
